import React from "react";
import { ApolloError, DocumentNode, useApolloClient, useQuery } from "@apollo/client";
import {
  GetCurbsuiteTransactionsData,
  CurbsuiteTransactionLogEntry,
} from "common/graphql/queries/GetCurbsuiteTransactions";

export interface TransactionsQueryResult {
  data: GetCurbsuiteTransactionsData | undefined;
  loading: boolean;
  error?: ApolloError;
  fetchTotalCount: () => Promise<number>;
  fetchDataBatch: (offset: number, limit: number) => Promise<CurbsuiteTransactionLogEntry[]>;
}

/**
 * Shared query/export-batch wiring behind useCurbsuiteTransactionsData and
 * useBusStationTransactionsData: run the query for the on-screen page, plus the two fetch functions
 * the export toolbar uses to pull the full result set in batches (same query, full page range).
 * Both callers query financial_transaction_log_no_balance and get back the same result shape
 * (GetCurbsuiteTransactionsData), differing only in which document and variables they pass.
 *
 * `exportVariables` is `variables` minus `offset`/`limit`, memoized separately by the caller so it
 * only changes when the filter/sort/date-range actually changes -- NOT on every page turn. That
 * keeps fetchTotalCount/fetchDataBatch referentially stable across pagination, matching the
 * pre-refactor callbacks, which never depended on `page` in the first place (fetchTotalCount always
 * queries offset:0/limit:0; fetchDataBatch takes its own offset/limit arguments).
 */
export function useTransactionsQuery<TVars extends { offset: number; limit: number }>(
  query: DocumentNode,
  variables: TVars,
  exportVariables: Omit<TVars, "offset" | "limit">,
  skip: boolean,
): TransactionsQueryResult {
  const client = useApolloClient();

  const queryResult = useQuery<GetCurbsuiteTransactionsData, TVars>(query, {
    variables,
    fetchPolicy: "network-only",
    skip,
  });

  const fetchTotalCount = React.useCallback(async () => {
    const result = await client.query<GetCurbsuiteTransactionsData, TVars>({
      query,
      fetchPolicy: "network-only",
      variables: { ...exportVariables, offset: 0, limit: 0 } as TVars,
    });
    return result.data?.aggregate.aggregate.count || 0;
  }, [client, query, exportVariables]);

  const fetchDataBatch = React.useCallback(
    async (offset: number, limit: number) => {
      const result = await client.query<GetCurbsuiteTransactionsData, TVars>({
        query,
        fetchPolicy: "network-only",
        variables: { ...exportVariables, offset, limit } as TVars,
      });
      return result.data?.transactions || [];
    },
    [client, query, exportVariables],
  );

  return { ...queryResult, fetchTotalCount, fetchDataBatch };
}
